import React, { type KeyboardEvent, type Ref, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { InteractiveIcon, Thumbnail } from '@bynder/design-system';
import generateTestId from 'packages/helpers/testIdHelpers';
import { color2ColorParams, colorToCSS } from 'packages/common/editor/editorHelper';
import { Color, Gradient, SolidColor } from 'packages/ds/ColorPicker/types';
import type { TriggerAriaProps, TriggerRenderProps } from './shared';
import { GradientThumbnail } from 'packages/ds/GradientThumbnail/GradientThumbnail';
import { isGradientColor } from '../../utils';
import { StyledInput } from './ColorInput.styled';

type Props = {
    isMixed?: boolean;
    id: string;
    defaultValue: Color;
    label: string;
    /** Legacy popover only. `ColorPickerV2` tracks drag state internally. */
    isColorDragging?: boolean;
    onChange: (value: Color) => void;
    testId: string;
    isDisabled?: boolean;
    onClick: TriggerRenderProps['onClick'];
    triggerRef: TriggerRenderProps['ref'];
    triggerProps: TriggerAriaProps;
};

export type ColorPickerTriggerRef = {
    setValue: (value: Color) => void;
};

export const ColorPickerTrigger = React.forwardRef<ColorPickerTriggerRef, Props>(
    (
        {
            onClick,
            onChange,
            isColorDragging,
            triggerRef,
            triggerProps,
            defaultValue,
            id,
            label,
            testId,
            isDisabled,
            isMixed,
        },
        ref,
    ) => {
        const [value, setValue] = useState<Color>(defaultValue);

        useImperativeHandle(
            ref,
            () => ({
                setValue,
            }),
            [],
        );

        useEffect(() => {
            if (!isColorDragging) {
                setValue(defaultValue);
            }
        }, [defaultValue]);

        // popup position (from react-laag) uses it for calculations
        // and not recalculates on ref change
        return (
            <div {...triggerProps} ref={triggerRef}>
                {isGradientColor(value) ? (
                    <GradientColorInput
                        onClick={onClick}
                        value={value}
                        id={id}
                        testId={testId}
                        label={label}
                        isMixed={isMixed}
                        isDisabled={isDisabled}
                    />
                ) : (
                    <SolidColorInput
                        onClick={onClick}
                        value={value}
                        onChange={onChange}
                        id={id}
                        testId={testId}
                        label={label}
                        isMixed={isMixed}
                        isDisabled={isDisabled}
                    />
                )}
            </div>
        );
    },
);

type SolidColorInputProps = {
    value: SolidColor;
    onChange: (color: Color) => void;
    isDisabled?: boolean;
    isMixed?: boolean;
    label: string;
    id: string;
    testId: string;
    onClick: () => void;
};

function SolidColorInput({ value, onChange, isDisabled, isMixed, testId, id, label, onClick }: SolidColorInputProps) {
    const [inputState, setInputState] = useState(getHexFromSolidColor(value, isMixed));

    useMemo(() => {
        setInputState(getHexFromSolidColor(value, isMixed));
    }, [value, isMixed]);

    const thumbnailColor = isMixed ? undefined : colorToCSS(color2ColorParams(value));

    const onFocus = () => {
        if (isMixed) {
            setInputState('#______');
        }
    };

    const onBlur = () => {
        let sanitizedValue = normalize(inputState);

        if (sanitizedValue.length > 7) {
            sanitizedValue = sanitizedValue.slice(0, 7);
        }

        // full hex #cccccc and short hex #ccc are valid
        if (sanitizedValue.length !== 7 && sanitizedValue.length !== 4) {
            setInputState(getHexFromSolidColor(value, isMixed));

            return;
        }

        sanitizedValue = makeHexFull(sanitizedValue);

        if (typeof value === 'string') {
            onChange(sanitizedValue);
        } else {
            onChange({ color: sanitizedValue, opacity: value.opacity });
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.blur();
        }
    };

    const handleChange = (raw: string) => {
        // Keep only # prefix and hex characters, limit to 7 chars (#XXXXXX)
        const filtered = '#' + raw.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);
        setInputState(filtered);
    };

    return (
        <StyledInput
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={isMixed ? 'Mixed' : ''}
            value={inputState}
            maxLength={7}
            isDisabled={isDisabled}
            $isUppercase={!(isMixed && inputState === '')}
            id={id}
            icon={
                <InteractiveIcon
                    label={label}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    icon={<Thumbnail variant="content" backgroundColor={thumbnailColor} shape="circle" size="xs" />}
                    isDisabled={isDisabled}
                />
            }
            aria-label={label}
            {...generateTestId(testId)}
        />
    );
}

type GradientColorInputProps = {
    onClick: () => void;
    value: Gradient;
    isDisabled?: boolean;
    isMixed?: boolean;
    label: string;
    id: string;
    testId: string;
};

function GradientColorInput({ value, isMixed, id, label, isDisabled, testId, onClick }: GradientColorInputProps) {
    return (
        // @ts-ignore
        <StyledInput
            id={id}
            icon={
                <InteractiveIcon
                    label={label}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    icon={<GradientThumbnail shape="circle" size="xs" {...value} />}
                    isDisabled={isDisabled}
                />
            }
            aria-label={label}
            value={isMixed ? 'Mixed' : ''}
            placeholder={value.type}
            isDisabled
            {...generateTestId(testId)}
        />
    );
}

function getHexFromSolidColor(color: SolidColor, isMixed?: boolean): string {
    if (isMixed) {
        return '';
    }

    if (typeof color === 'string') {
        return color;
    }

    return color.color;
}

function normalize(value: string) {
    const newValue = '#' + value.replace(/([^0-9A-F]+)/gi, '');

    return newValue.length === 1 ? '' : newValue;
}

const shortFormatHexRegex = /^#([a-f\d])([a-f\d])([a-f\d])$/i;
// longFormatHexReplacement is used to replace a match made by the regular expression.
// Converts a short-format hexadecimal color code(such as #0af) into a long-format hexadecimal color code(such as #00aaff).
const longFormatHexReplacement = '#$1$1$2$2$3$3';

function makeHexFull(color: string): string {
    let value = color;

    // Check if the hex value is in short-format
    if (shortFormatHexRegex.test(color)) {
        // Convert the short-format hex value to its long-format equivalent
        value = value.replace(shortFormatHexRegex, longFormatHexReplacement);
    }

    return value;
}
